import React, { useEffect, useState } from 'react';
import { Button } from 'reactstrap';
import PropTypes from 'prop-types';
import {
  setCallEnd,
  setCallNumber,
  setScreenType,
  useCallId,
  useCallingStatus,
  useCallNumber,
  useCallType,
  setHoldTime,
  useHoldTime,
  setHoldStartTime,
  useHoldStartTime,
  useCallEnd
} from '../../../store/slices/dialSlice';
import { useDispatch } from 'react-redux';
import { callHoldUnhold, callMuteUnmute } from '../../../store/slices/callingSlice';
import { useUserDetails } from '../../../store/slices/authSlice';
import { convertToMask } from '../../../utils/common';
import { useTranslation } from 'react-i18next';

const DefaultLiveCall = ({ number, setNumber }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const callType = useCallType();
  const callId = useCallId();
  const callNumber = useCallNumber();
  const user = useUserDetails();
  const callStatus = useCallingStatus();
  const holdTime = useHoldTime();
  const holdStartTime = useHoldStartTime();
  const callEnd = useCallEnd();

  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);

  const toggleMute = async () => {
    try {
      const payload = {
        uuid: callId,
        flag: isMuted ? 'un_mute' : 'mute',
        calltype: callType
      };
      await dispatch(callMuteUnmute(payload)).unwrap();
      setIsMuted(!isMuted);
    } catch (error) {
      console.log('error', error);
    }
  };

  const toggleHold = async () => {
    try {
      if (isOnHold) {
        const holdDuration = Math.floor((Date.now() - holdStartTime) / 1000);
        const newHoldTime = holdTime + holdDuration;
        dispatch(setHoldTime(newHoldTime));
        dispatch(setHoldStartTime(null));
      } else {
        dispatch(setHoldStartTime(Date.now()));
      }

      const payload = {
        uuid: callId,
        flag: isOnHold ? 'un_hold' : 'hold',
        calltype: callType
      };
      await dispatch(callHoldUnhold(payload)).unwrap();
      setIsOnHold(!isOnHold);
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if ((callStatus === 'WRAPUP' || callEnd) && isOnHold && holdStartTime !== null) {
      const holdDuration = Math.floor((Date.now() - holdStartTime) / 1000);
      const newHoldTime = holdTime + holdDuration;
      dispatch(setHoldTime(newHoldTime));
    }
  }, [callEnd, callStatus]);

  const handleEndCall = () => {
    if (isOnHold && holdStartTime !== null) {
      const holdDuration = Math.floor((Date.now() - holdStartTime) / 1000);
      const newHoldTime = holdTime + holdDuration;
      dispatch(setHoldTime(newHoldTime));
    }

    dispatch(setCallEnd(true));
    setNumber('');
    dispatch(setCallNumber(''));
    dispatch(setScreenType(null));
  };

  return (
    <div className="h-100 call-action-btn-container">
      <div className="mt-auto mb-auto text-white text-center fw-bold fs-4">
        {user?.process_details?.numberMasking === 'true'
          ? convertToMask(callNumber || number)
          : callNumber || number}
      </div>

      <div className="text-center text-white live-call-button-grid">
        <div className="d-flex flex-column align-items-center">
          <Button
            color={isMuted ? 'secondary' : 'transparent'}
            onClick={toggleMute}
            className={`mb-1 ${isMuted ? 'active' : ''}`}>
            <i className={`bi ${isMuted ? 'bi-mic-mute-fill' : 'bi-mic-fill'}`} />
          </Button>
          <span>{isMuted ? t('Unmute') : t('Mute')}</span>
        </div>
        <div className="d-flex flex-column align-items-center">
          <Button color="dark" className="mb-1" onClick={() => dispatch(setScreenType('DTMF'))}>
            <i className="bi bi-telephone-plus-fill" />
          </Button>
          <span>{t('DTMF')}</span>
        </div>
        <div className="d-flex flex-column align-items-center">
          <Button
            color={isOnHold ? 'success' : 'transparent'}
            onClick={toggleHold}
            className={`mb-1 ${isOnHold ? 'active' : ''}`}>
            <i className={`bi ${isOnHold ? 'bi-play-fill' : 'bi-pause-fill'}`} />
          </Button>
          <span>{isOnHold ? t('Resume') : t('Hold')}</span>
        </div>
        <div className="d-flex flex-column align-items-center">
          <Button
            color="info"
            className="mb-1"
            disabled={!user?.internaltransfer || callStatus !== 'ON CALL'}
            onClick={() => dispatch(setScreenType('TRANSFER'))}>
            <i className="bi bi-person-plus-fill" />
          </Button>
          <span>{t('Transfer')}</span>
        </div>
        <div className="d-flex flex-column align-items-center">
          <Button
            color="warning"
            className="mb-1"
            disabled={callStatus !== 'ON CALL'}
            onClick={() => dispatch(setScreenType('CONFERENCE'))}>
            <i className="bi bi-person-lines-fill" />
          </Button>
          <span>{t('Conference')}</span>
        </div>
      </div>
      <div className="text-center mt-auto pb-4">
        <Button color="danger" className="call-end-btn" onClick={handleEndCall}>
          <i className="bi bi-telephone-x-fill" />
        </Button>
      </div>
    </div>
  );
};

DefaultLiveCall.propTypes = {
  number: PropTypes.string,
  setNumber: PropTypes.func
};

export default DefaultLiveCall;
